import { ReactNode, useCallback, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./ToolTip.css";

export type ToolTipPlacement = "top" | "bottom" | "left" | "right";

interface IProps {
    // The tooltip body. Unlike a native `title` this can be any markup, not just text.
    content?: ReactNode
    children: ReactNode
    // Preferred side; flips automatically when it doesn't fit. Default "top".
    placement?: ToolTipPlacement
    // Class for the inline wrapper that hosts the hover area.
    className?: string
    maxWidth?: number
    disabled?: boolean
}

const GAP = 6;   // distance between the trigger and the tooltip
const EDGE = 4;  // keep at least this much space from the viewport edges

// Drop-in replacement for the native `title` attribute: same plain look, but it
// accepts markup and appears the instant the pointer arrives instead of after the
// browser's ~1s delay. Rendered in a portal on <body> with fixed positioning, so
// it is never clipped by a scroll container and always sits above drawers/panels.
export default function ToolTip(props: IProps) {

    const triggerRef = useRef<HTMLSpanElement>(null)
    const tipRef = useRef<HTMLDivElement>(null)

    const [open, openSet] = useState<boolean>(false)
    const [pos, posSet] = useState<{ left: number, top: number }>()

    const hide = useCallback(() => openSet(false), [])

    // The box is rendered hidden first so its real size can be measured, then
    // placed. useLayoutEffect runs before paint, so there is no visible jump.
    useLayoutEffect(() => {
        if (!open) {
            posSet(undefined)
            return
        }

        const trigger = triggerRef.current?.getBoundingClientRect()
        const tip = tipRef.current?.getBoundingClientRect()
        if (!trigger || !tip) return

        const vw = window.innerWidth, vh = window.innerHeight
        let placement = props.placement ?? "top"

        // Flip to the opposite side when the preferred one has no room.
        if (placement == "top" && trigger.top - GAP - tip.height < EDGE) placement = "bottom"
        else if (placement == "bottom" && trigger.bottom + GAP + tip.height > vh - EDGE) placement = "top"
        else if (placement == "left" && trigger.left - GAP - tip.width < EDGE) placement = "right"
        else if (placement == "right" && trigger.right + GAP + tip.width > vw - EDGE) placement = "left"

        let left: number, top: number
        switch (placement) {
            case "bottom":
                left = trigger.left + trigger.width / 2 - tip.width / 2
                top = trigger.bottom + GAP
                break
            case "left":
                left = trigger.left - GAP - tip.width
                top = trigger.top + trigger.height / 2 - tip.height / 2
                break
            case "right":
                left = trigger.right + GAP
                top = trigger.top + trigger.height / 2 - tip.height / 2
                break
            default:
                left = trigger.left + trigger.width / 2 - tip.width / 2
                top = trigger.top - GAP - tip.height
        }

        // Keep the whole box inside the viewport.
        left = Math.min(Math.max(left, EDGE), Math.max(EDGE, vw - tip.width - EDGE))
        top = Math.min(Math.max(top, EDGE), Math.max(EDGE, vh - tip.height - EDGE))

        posSet({ left, top })
        // props.content is a dep because a different body measures differently.
    }, [open, props.placement, props.content])

    // Scrolling or resizing moves the trigger out from under the box, so close it
    // instead of trying to follow. Capture catches scrolls of inner containers too,
    // which don't bubble.
    useLayoutEffect(() => {
        if (!open) return
        window.addEventListener("scroll", hide, true)
        window.addEventListener("resize", hide)
        return () => {
            window.removeEventListener("scroll", hide, true)
            window.removeEventListener("resize", hide)
        }
    }, [open, hide])

    // Declared after every hook: this project's ESLint flags consts placed between
    // hooks as a rules-of-hooks violation.
    const enabled = !props.disabled && props.content !== undefined
        && props.content !== null && props.content !== ""

    return (
        <>
            <span ref={triggerRef} className={`tooltip-trigger ${props.className ?? ""}`}
                onMouseEnter={() => { if (enabled) openSet(true) }}
                onMouseLeave={hide}
                onMouseDown={hide}>
                {props.children}
            </span>
            {
                open && enabled && createPortal(
                    <div ref={tipRef} role="tooltip" className="tooltip-box"
                        style={{
                            left: pos?.left ?? 0,
                            top: pos?.top ?? 0,
                            maxWidth: props.maxWidth ?? 260,
                            // Hidden for the one measuring pass, before pos is known.
                            visibility: pos ? "visible" : "hidden"
                        }}>
                        {props.content}
                    </div>,
                    document.body)
            }
        </>
    )
}
